import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api';
import ProfileCard from '../components/ProfileCard';

const TIER_LABEL = { bronze: 'Bronze', silver: 'Argent', gold: 'Or', platinum: 'Platine' };

export function BadgeCard({ b }) {
  return (
    <div className={`badge-card tier-${b.tier}${b.unlocked ? ' unlocked' : ''}`}>
      <div className="badge-icon">{b.unlocked ? b.icon : '🔒'}</div>
      <div className="badge-info">
        <div className="badge-title">{b.title}</div>
        <div className="muted small">{b.desc}</div>
        {b.unlocked ? (
          <div className="badge-date">✔ Obtenu le {new Date(b.unlocked_at).toLocaleDateString('fr-FR')} · +{b.points} pts</div>
        ) : (
          <>
            <div className="progress-bar"><div className="progress-fill" style={{ width: `${Math.round(b.progress * 100)}%` }} /></div>
            <div className="muted small">{b.current.toLocaleString('fr-FR')} / {b.target.toLocaleString('fr-FR')}</div>
          </>
        )}
      </div>
      <span className="tier-tag">{TIER_LABEL[b.tier]}</span>
    </div>
  );
}

function StrengthSection({ strength, badges }) {
  const { lifts, profile, levels } = strength;
  const fmt = (v) => String(Math.round(v * 10) / 10).replace('.', ',');
  return (
    <section>
      <h2 className="section-title">Standards de force</h2>
      {profile.needs_bodyweight ? (
        <div className="card muted">Renseigne ton poids de corps (page <Link to="/body">Suivi corporel</Link>) pour activer les trophées de force.</div>
      ) : (
        <>
          <p className="muted small" style={{ marginBottom: 12 }}>
            {profile.sex === 'F' ? 'Femme' : 'Homme'}{profile.age != null ? ` · ${profile.age} ans` : ''} · {fmt(profile.bodyweight)} kg
            {profile.age_factor < 1 ? ` · ajustement d'âge ×${String(profile.age_factor).replace('.', ',')}` : ''}.
            Le niveau se base sur le 1RM estimé : une charge sur peu de reps vaut une charge plus légère sur plus de reps.
          </p>
          <div className="badge-grid">
            {lifts.map((l) => {
              const got = badges.filter((b) => b.lift_key === l.key && b.unlocked).length;
              return (
                <div key={l.key} className={`badge-card strength-card${l.level_index >= 0 ? ' unlocked' : ''} tier-${['bronze', 'bronze', 'silver', 'gold', 'platinum'][Math.max(l.level_index, 0)]}`}>
                  <div className="badge-icon">{l.icon}</div>
                  <div className="badge-info">
                    <div className="badge-title">{l.name}</div>
                    <div className="badge-desc">
                      {l.level_name ? `Niveau ${l.level_name}` : 'Pas encore classé'} · 1RM estimé {l.e1rm ? `${fmt(l.e1rm)} kg` : '—'}
                    </div>
                    <div className="strength-steps">
                      {levels.map((name, i) => <span key={name} className={`strength-step${i < got ? ' on' : ''}`} title={name} />)}
                    </div>
                    <div className="badge-date">
                      {l.next_level ? `Prochain : ${l.next_level} à ${fmt(l.next_target)} kg` : l.level_index >= levels.length - 1 ? '🏆 Niveau maximum' : ''}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}

export default function Trophies() {
  const { data, isLoading } = useQuery({ queryKey: ['achievements'], queryFn: api.getAchievements });
  if (isLoading || !data) return <div className="loading"><div className="spinner" />Chargement…</div>;

  const { badges, level } = data;
  const unlocked = badges.filter((b) => b.unlocked).length;
  const span = level.next_at ? level.next_at - level.current_at : 1;
  const pct = level.next_at ? Math.min(100, ((level.points - level.current_at) / span) * 100) : 100;
  const categories = [...new Set(badges.filter((b) => !b.strength).map((b) => b.category))];

  return (
    <div>
      <h1 className="page-title">Trophées &amp; badges</h1>
      <p className="page-subtitle">{unlocked} / {badges.length} badges débloqués</p>

      <div className="card level-card">
        <div className="level-head">
          <div className="level-badge">{level.index}</div>
          <div>
            <div className="level-name">{level.name}</div>
            <div className="muted small">{level.points} points{level.next_at ? ` · prochain niveau « ${level.next_name} » à ${level.next_at} pts` : ' · niveau maximum'}</div>
          </div>
        </div>
        <div className="progress-bar"><div className="progress-fill" style={{ width: `${pct}%` }} /></div>
      </div>

      <ProfileCard />

      <StrengthSection strength={data.strength} badges={badges} />

      {categories.map((cat) => (
        <section key={cat}>
          <h2 className="section-title">{cat}</h2>
          <div className="badge-grid">
            {badges.filter((b) => b.category === cat).map((b) => <BadgeCard key={b.id} b={b} />)}
          </div>
        </section>
      ))}
    </div>
  );
}
